import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getContrastColor } from '../services/utils';

// Základní typ pro volby, se kterým komponenta pracuje interně.
type SelectOption = { id?: number, name: string, color?: string };

// Props jsou rozšířeny, aby akceptovaly i jednodušší formáty voleb, jako je pole řetězců.
interface SearchableSelectProps<T extends SelectOption> {
  value: T | T[] | string | string[] | undefined;
  onChange: (newValue: T | T[]) => void;
  options: ReadonlyArray<T> | ReadonlyArray<string>; // Akceptuje pole objektů nebo pole řetězců
  placeholder?: string;
  isMulti?: boolean;
  onAddItem?: (newItem: { name: string; color?: string }) => Promise<T>;
}

const XButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="ml-1 text-current hover:opacity-75 flex-shrink-0"
        aria-label="Odstranit"
    >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    </button>
);


export const SearchableSelect = <T extends SelectOption>({ value, onChange, options, placeholder, isMulti = false, onAddItem }: SearchableSelectProps<T>) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Normalizuje `options` prop, aby komponenta mohla vždy pracovat s konzistentní strukturou objektů.
  // Pokud je předáno pole řetězců, převede se na pole objektů.
  const processedOptions = useMemo((): T[] => {
    if (!options || options.length === 0) return [];
    if (typeof options[0] === 'string') {
      return (options as string[]).map(name => ({ name } as T));
    }
    return options as T[];
  }, [options]);


  // Zajišťuje, že zobrazené vybrané položky mají vždy aktuální data (např. barvu)
  // z hlavního seznamu 'processedOptions'.
  const currentMultiValue = useMemo((): T[] => {
    if (!isMulti) return [];
    const valueAsArray = Array.isArray(value) ? value : value ? [value] : [];
    
    return valueAsArray
      .map(v => {
          const nameToFind = typeof v === 'string' ? v : v.name;
          // Vždy vyhledá čerstvou verzi položky z 'processedOptions', aby se projevily změny.
          return processedOptions.find(o => o.name === nameToFind);
      })
      .filter((v): v is T => !!v); // Odfiltruje položky, které nebyly nalezeny (např. smazané)
  }, [value, processedOptions, isMulti]);


    // Tento efekt synchronizuje externí hodnotu (prop 'value') do interního stavu textového pole.
    useEffect(() => {
        if (isMulti) return;

        if (value && typeof value === 'object' && 'name' in value) {
            setInputValue(value.name);
        } else {
            // Pokud je externí hodnota smazána (např. nastavena na null), vymaže se i text v poli.
            setInputValue('');
        }
    }, [value, isMulti]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                 // Při kliknutí mimo komponentu se text v poli vrátí na hodnotu vybrané položky,
                // aby se předešlo neuloženému, rozepsanému textu.
                if (!isMulti) {
                    if (value && typeof value === 'object' && 'name' in value) {
                        setInputValue(value.name);
                    } else {
                        setInputValue('');
                    }
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [wrapperRef, value, isMulti]);


  const handleRemoveItem = (itemToRemove: T) => {
    if (isMulti) {
      onChange(currentMultiValue.filter(item => item.name !== itemToRemove.name));
    }
  };
  
  const handleSelect = (option: T) => {
    if (isMulti) {
        if (!currentMultiValue.some(item => item.name === option.name)) {
            onChange([...currentMultiValue, option]);
        }
        setInputValue('');
        inputRef.current?.focus();
    } else {
        onChange(option);
        setInputValue(option.name);
        setIsOpen(false);
    }
  };
  
  const filteredOptions = processedOptions.filter(option =>
    option.name.toLowerCase().includes(inputValue.toLowerCase()) && 
    (!isMulti || !currentMultiValue.some(v => v.name === option.name))
  );

  const showAddNew = onAddItem && inputValue && !processedOptions.some(opt => opt.name.toLowerCase() === inputValue.toLowerCase());

  const handleAddNew = async () => {
    if (!showAddNew || !inputValue) return;

    const tempNewItem = { name: inputValue, color: '#60A5FA' };

    if (onAddItem) {
        try {
            const persistedItem = await onAddItem(tempNewItem);
            handleSelect(persistedItem);
        } catch (error) {
            console.error("Nepodařilo se přidat novou položku:", error);
        }
    }
  };

    // Opravená funkce pro získání zobrazované hodnoty v režimu jednoho výběru.
    // Nyní vždy vrací interní stav 'inputValue', což umožňuje uživateli plynule psát.
    const getDisplayValue = () => {
        if (isMulti) return '';
        return inputValue;
    };


  return (
    <div className="relative w-full" ref={wrapperRef}>
      {isMulti ? (
          <div 
              className="p-1 border border-gray-300 rounded-md w-full flex flex-wrap items-center gap-1 bg-white cursor-text"
              onClick={() => inputRef.current?.focus()}
          >
              {currentMultiValue.map(item => (
                  <span 
                      key={item.id || item.name} 
                      className="text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1"
                      style={{ backgroundColor: item.color || '#E5E7EB', color: getContrastColor(item.color) }}
                  >
                      <span>{item.name}</span>
                      <XButton onClick={() => handleRemoveItem(item)} />
                  </span>
              ))}
              <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onFocus={() => setIsOpen(true)}
                  onKeyDown={(e) => {
                      if (e.key === 'Enter' && inputValue && showAddNew) {
                          e.preventDefault();
                          handleAddNew();
                      }
                  }}
                  placeholder={currentMultiValue.length === 0 ? placeholder : ''}
                  className="flex-grow p-1 border-none focus:ring-0 text-sm"
                  autoComplete="off"
              />
          </div>
      ) : (
          <input
              type="text"
              value={getDisplayValue()}
              onChange={(e) => { setInputValue(e.target.value); if (!isOpen) setIsOpen(true); }}
              onFocus={() => setIsOpen(true)}
              placeholder={placeholder}
              className="p-1 border border-gray-300 rounded-md w-full"
              autoComplete="off"
          />
      )}
      {isOpen && (
        <ul className="absolute z-20 min-w-full max-w-sm bg-white border border-gray-300 rounded-md mt-1 max-h-60 overflow-y-auto shadow-lg">
          {filteredOptions.map(option => (
            <li
              key={option.id || option.name}
              onClick={() => handleSelect(option)}
              className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm flex items-center whitespace-nowrap"
            >
              {option.color && (
                <span 
                    className="w-3 h-3 rounded-full mr-2 flex-shrink-0" 
                    style={{ backgroundColor: option.color }}
                ></span>
              )}
              <span>{option.name}</span>
            </li>
          ))}
          {showAddNew && (
            <li
              onClick={handleAddNew}
              className="px-3 py-2 cursor-pointer hover:bg-blue-100 text-blue-600 text-sm whitespace-nowrap"
            >
              Přidat "{inputValue}"
            </li>
          )}
          {filteredOptions.length === 0 && !showAddNew && (
             <li className="px-3 py-2 text-gray-500 text-sm whitespace-nowrap">Nenalezeno</li>
          )}
        </ul>
      )}
    </div>
  );
};